import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { addProject, getCustomers, getCostCenters, getTaxes, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { getAuthUserData, getReadableError, hasAnyPermission } from '../../../../shared/utils.js/permissionUtils';


// add project component
const AddProject = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customers, customersPagination, costCenters, costCentersPagination, taxes, taxesPagination, isLoading, error, success, user } = useSelector((s) => s.auth);
  const canCreateProject = hasAnyPermission(getAuthUserData(user), 'projects.create');
  const lastErrorRef = useRef({ message: null, time: 0 });

  const [form, setForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });


  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  // Cost Center State variables
  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Tax State variables
  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);


  // useEffects for fetching dropdown data
  useEffect(() => {
    if (customerDropdownOpen) {
      dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCurrentPage, customerDropdownOpen, customerSearchTerm]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchTerm, customerDropdownOpen]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterSearchTerm, costCenterDropdownOpen]);

  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxSearchTerm, taxDropdownOpen]);

  useEffect(() => {
    
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (!canCreateProject) {
      toast.error('ليس لديك صلاحية إضافة مشروع. المطلوب: projects.create', { rtl: true });
      navigate('/projects', { replace: true });
    }
  }, [canCreateProject, navigate]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      navigate('/projects');
    }
  }, [success, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      const message = getReadableError(error, 'تعذر حفظ المشروع');
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== message || now - last.time > 2000) {
        toast.error(message, { rtl: true });
        lastErrorRef.current = { message, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canCreateProject) {
      toast.error('ليس لديك صلاحية إضافة مشروع. المطلوب: projects.create', { rtl: true });
      return;
    }
    if (!form.code || !form.name_ar || !form.start_date || !form.end_date || !form.contract_value) {
      toast.error('برجاء استكمال الحقول المطلوبة', { rtl: true });
      return;
    }
    const payload = {
      code: form.code,
      name_ar: form.name_ar,
      name_en: form.name_en || undefined,
      customer_id: form.customer_id || null,
      start_date: form.start_date,
      end_date: form.end_date,
      status: form.status,
      contract_value: Number(form.contract_value),
      tax_id: form.tax_id || null,
      cost_center_id: form.cost_center_id || null,
      notes: form.notes || undefined,
    };
    dispatch(addProject(payload));
  };

  const renderDropdownItem = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = item[nameArField] || item[nameEnField] || '';
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderCustomerDisplay = () => {
    if (!form.customer_id) return 'اختر العميل';
    const selected = (customers || []).find((c) => String(c.id) === String(form.customer_id));
    return selected ? renderDropdownItem(selected, 'code', 'name_ar', 'name') : 'اختر العميل';
  };

  const renderCostCenterDisplay = () => {
    if (!form.cost_center_id) return 'اختر مركز التكلفة';
    const selected = (costCenters || []).find((cc) => String(cc.id) === String(form.cost_center_id));
    return selected ? renderDropdownItem(selected, 'code', 'name_ar', 'name') : 'اختر مركز التكلفة';
  };

  const renderTaxDisplay = () => {
    if (!form.tax_id) return 'اختر الضريبة';
    const selected = (taxes || []).find((t) => String(t.id) === String(form.tax_id));
    return selected ? `${selected.name_ar || selected.name_en || ''}` : 'اختر الضريبة';
  };

  const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label }) => {
    if (!isOpen) return null;
    return (
      <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4 }}>
        <div style={{ padding: 8 }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={`ابحث عن ${label}`}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
          />
        </div>
        {pagination && pagination.last_page >= 1 && (
          <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#ccc', fontSize: 11 }}>الصفحة {pagination.current_page} من {pagination.last_page}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map((page) => (
                  <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
                ));
              })()}
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
            </div>
          </div>
        )}
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {(Array.isArray(items) ? items : []).map((item) => (
            <div key={item.id} onClick={() => onSelect(item.id)} style={{ padding: 10, cursor: 'pointer', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{renderDropdownItem(item, 'code', 'name_ar', 'name')}</span>
            </div>
          ))}
          {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>لا توجد نتائج</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px' }}>إضافة مشروع</h1>
      <form onSubmit={handleSubmit} style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 24, display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الكود <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="text" name="code" value={form.code} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الاسم بالعربية <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="text" name="name_ar" value={form.name_ar} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الاسم بالإنجليزية</label>
            <input type="text" name="name_en" value={form.name_en} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>العميل</label>
            <div onClick={() => { setCustomerDropdownOpen((p) => !p); if (customerDropdownOpen) setCustomerSearchTerm(''); }} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
              <span>{renderCustomerDisplay()}</span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            <DropdownWithSearch isOpen={customerDropdownOpen} onToggle={setCustomerDropdownOpen} searchTerm={customerSearchTerm} onSearchChange={setCustomerSearchTerm} searchInputRef={customerSearchInputRef} items={customers || []} pagination={customersPagination} currentPage={customerCurrentPage} onPageChange={setCustomerCurrentPage} onSelect={(id) => { setForm((prev) => ({ ...prev, customer_id: id })); setCustomerDropdownOpen(false); setCustomerSearchTerm(''); setCustomerCurrentPage(1); }} displayValue={renderCustomerDisplay} placeholder="اختر العميل" label="عميل" />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>تاريخ البداية <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="date" name="start_date" value={form.start_date} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>تاريخ النهاية <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="date" name="end_date" value={form.end_date} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الحالة</label>
            <select name="status" value={form.status} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }}>
              <option value="planned">مخطط</option>
              <option value="in_progress">قيد التنفيذ</option>
              <option value="paused">متوقف</option>
              <option value="completed">مكتمل</option>
              <option value="cancelled">ملغى</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>قيمة العقد <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="number"  name="contract_value" value={form.contract_value} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الضريبة</label>
            <div onClick={() => { setTaxDropdownOpen((p) => !p); if (taxDropdownOpen) setTaxSearchTerm(''); }} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
              <span>{renderTaxDisplay()}</span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            <DropdownWithSearch isOpen={taxDropdownOpen} onToggle={setTaxDropdownOpen} searchTerm={taxSearchTerm} onSearchChange={setTaxSearchTerm} searchInputRef={taxSearchInputRef} items={taxes || []} pagination={taxesPagination} currentPage={taxCurrentPage} onPageChange={setTaxCurrentPage} onSelect={(id) => { setForm((prev) => ({ ...prev, tax_id: id })); setTaxDropdownOpen(false); setTaxSearchTerm(''); setTaxCurrentPage(1); }} displayValue={renderTaxDisplay} placeholder="اختر الضريبة" label="ضريبة" />
          </div>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>مركز التكلفة</label>
            <div onClick={() => { setCostCenterDropdownOpen((p) => !p); if (costCenterDropdownOpen) setCostCenterSearchTerm(''); }} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
              <span>{renderCostCenterDisplay()}</span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            <DropdownWithSearch isOpen={costCenterDropdownOpen} onToggle={setCostCenterDropdownOpen} searchTerm={costCenterSearchTerm} onSearchChange={setCostCenterSearchTerm} searchInputRef={costCenterSearchInputRef} items={costCenters || []} pagination={costCentersPagination} currentPage={costCenterCurrentPage} onPageChange={setCostCenterCurrentPage} onSelect={(id) => { setForm((prev) => ({ ...prev, cost_center_id: id })); setCostCenterDropdownOpen(false); setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }} displayValue={renderCostCenterDisplay} placeholder="اختر مركز التكلفة" label="مركز تكلفة" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>ملاحظات</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical' }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button type="button" onClick={() => navigate('/projects')} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>إلغاء</button>
          <button type="submit" disabled={isLoading || !canCreateProject} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: isLoading || !canCreateProject ? 'not-allowed' : 'pointer', opacity: isLoading || !canCreateProject ? 0.7 : 1 }}>{isLoading ? 'جاري الحفظ...' : 'حفظ'}</button>
        </div>
      </form>
    </div>
  );
};

export default AddProject;
